import intersect from '@turf/line-intersect';
import {point, lineString} from '@turf/helpers';

interface UpdateParams {
    id: string;
    bounds: mapboxgl.LngLatBounds;
    zoom: number;
    coordinateGridSpacing: number[];
    labelFunc: (props: any) => string;
}

export function getLineIntersection(
    l1: GeoJSON.LineString | GeoJSON.Feature<GeoJSON.LineString>,
    l2: GeoJSON.LineString | GeoJSON.Feature<GeoJSON.LineString>
) {
    const intersection = intersect(l1, l2);
    if (!intersection.features.length) return [];
    return intersection.features[0].geometry.coordinates;
}

export function makeLayers(): (mapboxgl.LineLayer | mapboxgl.SymbolLayer)[] {
    return [
        {
            id: 'graticule_line',
            type: 'line',
            source: 'graticule',
            minzoom: 3,
            filter: ['==', '$type', 'LineString'],
            paint: {
                'line-width': 1,
                'line-color': '#3C8CC8',
                'line-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 12, 1],
            },
        },
        {
            id: 'graticule_label',
            type: 'symbol',
            source: 'graticule',
            minzoom: 3,
            filter: ['==', '$type', 'Point'],
            layout: {
                'text-field': ['get', 'label'],
                'text-anchor': [
                    'case',
                    ['==', ['get', 'direction'], 'north'],
                    'top',
                    ['==', ['get', 'direction'], 'south'],
                    'bottom',
                    ['==', ['get', 'direction'], 'west'],
                    'left',
                    ['==', ['get', 'direction'], 'east'],
                    'right',
                    'center',
                ],
                'text-size': 16,
                'text-offset': [
                    'case',
                    ['==', ['get', 'direction'], 'north'],
                    ['literal', [0, 0.35]],
                    ['==', ['get', 'direction'], 'south'],
                    ['literal', [0, -0.1]],
                    ['==', ['get', 'direction'], 'west'],
                    ['literal', [0.5, 0]],
                    ['==', ['get', 'direction'], 'east'],
                    ['literal', [-0.5, 0]],
                    ['literal', [0, 0]],
                ],
                'text-font': ['Regular'],
            },
            paint: {
                'text-halo-color': 'rgba(255,255,255,1)',
                'text-halo-width': 2,
            },
        },
    ];
}

function makeLabels(
    feature: GeoJSON.Feature<any, {deg: number; type: string}>,
    bounds: mapboxgl.LngLatBounds,
    formatLabel: (props: {deg: number}) => string
): GeoJSON.Feature<GeoJSON.Point>[] {
    const features = [];
    const properties = {
        ...feature.properties,
        label: formatLabel(feature.properties),
    };

    if (feature.properties.type === 'meridian') {
        const north = lineString([
            [bounds.getWest(), bounds.getNorth()],
            [bounds.getEast(), bounds.getNorth()],
        ]);
        const south = lineString([
            [bounds.getWest(), bounds.getSouth()],
            [bounds.getEast(), bounds.getSouth()],
        ]);

        const northIntersection = getLineIntersection(feature, north);
        if (northIntersection.length > 0) {
            features.push(
                point(northIntersection, {
                    ...properties,
                    direction: 'north',
                })
            );
        }

        const southIntersection = getLineIntersection(feature, south);
        if (southIntersection.length > 0) {
            features.push(
                point(southIntersection, {
                    ...properties,
                    direction: 'south',
                })
            );
        }
    } else {
        const west = lineString([
            [bounds.getWest(), bounds.getSouth()],
            [bounds.getWest(), bounds.getNorth()],
        ]);
        const east = lineString([
            [bounds.getEast(), bounds.getNorth()],
            [bounds.getEast(), bounds.getSouth()],
        ]);

        const westIntersection = getLineIntersection(feature, west);
        if (westIntersection.length > 0) {
            features.push(
                point(westIntersection, {
                    ...properties,
                    direction: 'west',
                })
            );
        }
        const eastIntersection = getLineIntersection(feature, east);
        if (eastIntersection.length > 0) {
            features.push(
                point(getLineIntersection(feature, east), {
                    ...properties,
                    direction: 'east',
                })
            );
        }
    }

    return features;
}

export function makeMeridian(
    lng: number,
    minLat: number,
    maxLat: number,
    interval: number
): GeoJSON.Position[] {
    // Make sure that we always return two coordinates
    if (maxLat === minLat) {
        maxLat += interval;
    }
    const coords = [];
    for (let lat = minLat; lat <= maxLat; lat += interval) {
        coords.push([lng, lat]);
    }
    return coords;
}

export function makeParallel(
    lat: number,
    minLng: number,
    maxLng: number,
    interval: number
): GeoJSON.Position[] {
    const coords = [];
    for (let lng = minLng; lng <= maxLng; lng += interval) {
        coords.push([lng, lat]);
    }
    return coords;
}

function snap(coord: number, interval: number) {
    return Math.ceil(coord / interval) * interval;
}

export function makeSources(features: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Point>[] = []) {
    return {
        graticule: {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: features,
            },
        } as mapboxgl.GeoJSONSourceRaw,
    };
}

export function update({
    id,
    bounds,
    zoom,
    coordinateGridSpacing,
    labelFunc,
}: UpdateParams): mapboxgl.Style {
    zoom = Math.floor(zoom);
    const interval = coordinateGridSpacing[zoom] || coordinateGridSpacing[18];

    const features: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Point>[] = [];

    // Buffer the bounding box by 50% to improve map pan UX
    const bufferedBounds = crudeBuffer(bounds);

    // Probably want to normalize lngs to be between -180 and 180?
    const minLng = snap(bufferedBounds[0], interval) - interval;
    const maxLng = snap(bufferedBounds[2], interval) + interval;

    const minLat = snap(bufferedBounds[1], interval) - interval;
    const maxLat = snap(bufferedBounds[3], interval) + interval;

    for (let lng = minLng; lng <= maxLng; lng += interval) {
        const coords = makeMeridian(lng, minLat, maxLat, interval);

        if (coords.length > 0) {
            const line = lineString(coords, {
                deg: lng,
                type: 'meridian',
            });
            const labels = makeLabels(line, bounds, labelFunc);
            features.push(line, ...labels);
        }
    }

    for (let lat = minLat; lat <= maxLat; lat += interval) {
        const coords = makeParallel(lat, minLng, maxLng, interval);

        if (coords.length > 0) {
            const line = lineString(coords, {
                deg: lat,
                type: 'parallel',
            });
            const labels = makeLabels(line, bounds, labelFunc);
            features.push(line, ...labels);
        }
    }

    return {
        version: 8,
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
        metadata: {
            id: id,
        },
        sources: makeSources(features),
        layers: makeLayers(),
    };
}

function crudeBuffer(bounds: mapboxgl.LngLatBounds): [number, number, number, number] {
    const xBuffer = Math.abs(bounds.getEast() - bounds.getWest()) * 0.5;
    const yBuffer = Math.abs(bounds.getNorth() - bounds.getSouth()) * 0.5;

    return [
        bounds.getWest() - xBuffer,
        bounds.getSouth() - yBuffer,
        bounds.getEast() + xBuffer,
        bounds.getNorth() + yBuffer,
    ];
}
